import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/i18n/I18nContext";
import { toNlPath, NL_SLUGS } from "@/i18n/slugs";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyMobileCta } from "@/components/StickyMobileCta";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PricingPage from "@/pages/PricingPage";
import IndustriesPage from "@/pages/IndustriesPage";
import AboutPage from "@/pages/AboutPage";
import DashboardPage from "@/pages/DashboardPage";
import ContactPage from "@/pages/ContactPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import NotFoundPage from "@/pages/NotFoundPage";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Shell = ({ children, hideFooter = false }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;
    const els = Array.from(document.querySelectorAll("main .band, main section"))
      .filter((el) => !(el.tagName === "SECTION" && el.closest(".band")));
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("reveal-visible");
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;
      el.classList.add("reveal");
      obs.observe(el);
    });
    return () => obs.disconnect();
  }, [pathname]);
  return (
    <>
      <Navbar />
      <div key={pathname} className="route-fade">{children}</div>
      {!hideFooter && <Footer />}
      <StickyMobileCta />
    </>
  );
};

const publicPages = [
  { path: "/", Page: HomePage },
  { path: "/services", Page: ServicesPage },
  { path: "/how-it-works", Page: HowItWorksPage },
  { path: "/pricing", Page: PricingPage },
  { path: "/industries", Page: IndustriesPage },
  { path: "/about", Page: AboutPage },
  { path: "/dashboard", Page: DashboardPage },
  { path: "/contact", Page: ContactPage },
  { path: "/privacy", Page: PrivacyPage },
  { path: "/terms", Page: TermsPage },
];

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <ScrollToTop />
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          {publicPages.map(({ path, Page }) => (
            <Route key={path} path={path} element={<Shell><Page /></Shell>} />
          ))}
          {publicPages.map(({ path, Page }) => (
            <Route key={`nl${path}`} path={toNlPath(path)} element={<Shell><Page /></Shell>} />
          ))}
          {Object.entries(NL_SLUGS).map(([en, nl]) => (
            <Route key={`nlredir${en}`} path={`/nl${en}`} element={<Navigate to={`/nl${nl}`} replace />} />
          ))}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<Shell hideFooter><AdminPanelPage /></Shell>} />
          <Route path="*" element={<Shell><NotFoundPage /></Shell>} />
        </Routes>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
